import React, { useMemo } from 'react';
import withErrorBoundary from '@/utils/errorBoundary';
import * as Styles from './FastCheckoutUserData.styles';
import {
  IconCheck, IconLoading, Link, PhoneInput, SelectField, TextField,
} from 'gatorcomponents';
import { CEP_URL_BY_BRAND } from '@/config/urls/cepUrls';
import { config, COUNTRY } from '@/config';
import { isBRServer } from '@/utils/Validators/validation';
import { COUNTRY_DOCUMENT_RULES, POSTAL_CODE_LENGTH } from '../../../FastCheckout.enum';

const FastCheckoutUserData = ({
  brand,
  countriesSelectOptions,
  countryCode,
  disableUserDataFields,
  formData,
  handleFormChange,
  isBRCountry,
  isCompany,
  isForeigner,
  loadingZipCode,
  locale,
  validateField,
}) => {
  const {
    document, nacionality, birthDate, name, telephone, country, cep, postalCode, state, city, address, block, complement, foreigner, companyName, companyResponsible, company,
  } = locale.fields;

  const getCepUrl = useMemo(() => CEP_URL_BY_BRAND[config.COUNTRY], []);
  const countriesSelectOptionsMemo = useMemo(() => countriesSelectOptions, [countriesSelectOptions]);
  const renderAddressFields = useMemo(() => (
    !isBRCountry || (formData.city.value.name && formData.state.value.name && !formData.postalCode.error)
  ), [isBRCountry, formData.city.value.name, formData.state.value.name, formData.postalCode.error]);

  return (
    <Styles.Content>
      {isBRServer && (
        <Styles.CheckboxWrapper onClick={() => handleFormChange('foreigner', !isForeigner)} $disabled={isCompany || disableUserDataFields}>
          <Styles.CheckboxInputAdapter type="button">
            <Styles.CheckboxInput
              id="isForeigner"
              type="checkbox"
              checked={isForeigner}
              data-testid="isForeigner"
              readOnly
            />
            <Styles.Checkbox checked={isForeigner}>
              {isForeigner && <IconCheck color="highPure" />}
            </Styles.Checkbox>
          </Styles.CheckboxInputAdapter>
          <Styles.CheckboxLabel>
            {foreigner.label}
          </Styles.CheckboxLabel>
        </Styles.CheckboxWrapper>
      )}
      {!isBRServer && (
        <Styles.CheckboxWrapper onClick={() => handleFormChange('isCompany', !isCompany)} $disabled={disableUserDataFields}>
          <Styles.CheckboxInputAdapter type="button">
            <Styles.CheckboxInput
              id="isCompany"
              type="checkbox"
              checked={isCompany}
              data-testid="isCompany"
              readOnly
            />
            <Styles.Checkbox checked={isCompany}>
              {isCompany && <IconCheck color="highPure" />}
            </Styles.Checkbox>
          </Styles.CheckboxInputAdapter>
          <Styles.CheckboxLabel>
            {company.label}
          </Styles.CheckboxLabel>
        </Styles.CheckboxWrapper>
      )}
      <Styles.FormLine>
        {(isForeigner && countriesSelectOptionsMemo.length > 0) && (
          <Styles.NacionalityWrapper>
            <SelectField
              withScroll
              options={countriesSelectOptionsMemo}
              positionAbsolute
              id="birthCountry"
              name="birthCountry"
              onClickSelect={fieldData => handleFormChange('birthCountry', fieldData.value)}
              onBlur={({ name, value }) => validateField(name, value)}
              placeholder={nacionality.label}
              value={formData.birthCountry.value.name}
              filterable
              disabled={disableUserDataFields}
            />
          </Styles.NacionalityWrapper>
        )}
        <Styles.DocumentWrapper $isForeigner={isForeigner}>
          <TextField
            id="document"
            name="document"
            label={document.label}
            value={formData.document.value}
            onChange={fieldData => handleFormChange('document', fieldData.currentTarget.value)}
            onBlur={({ name, value }) => validateField(name, value)}
            mask={(isForeigner || !isBRServer) ? 'noSpecialCharacters' : 'document'}
            helpText={formData.document.error}
            status={formData.document.error ? 'error' : 'default'}
            disabled={disableUserDataFields}
            maxLength={(() => {
              if (isBRServer) return;
              const rule = COUNTRY_DOCUMENT_RULES[COUNTRY];
              return rule ? rule.max : undefined;
            })()}
          />
        </Styles.DocumentWrapper>
        {!isCompany && (
          <Styles.BirthdayWrapper $isForeigner={isForeigner}>
            <TextField
              label={birthDate.label}
              id="birthDate"
              name="birthDate"
              onChange={fieldData => handleFormChange('birthDate', fieldData.currentTarget.value)}
              value={formData.birthDate.value}
              onBlur={({ name, value }) => validateField(name, value)}
              mask="date"
              maxLength={10}
              helpText={formData.birthDate.error}
              status={formData.birthDate.error ? 'error' : 'default'}
              disabled={disableUserDataFields}
            />
          </Styles.BirthdayWrapper>
        )}
      </Styles.FormLine>
      <Styles.FormLine>
        <Styles.NameWrapper>
          {!isCompany && (
            <TextField
              id="name"
              name="name"
              onChange={fieldData => handleFormChange('name', fieldData.currentTarget.value)}
              value={formData.name.value}
              label={name.label}
              onBlur={({ name, value }) => validateField(name, value)}
              helpText={formData.name.error}
              status={formData.name.error ? 'error' : 'default'}
              maxLength={isBRServer ? 40 : undefined}
              disabled={disableUserDataFields}
            />
          )}
          {isCompany && (
            <TextField
              id="companyName"
              name="companyName"
              onChange={fieldData => handleFormChange('companyName', fieldData.currentTarget.value)}
              label={companyName.label}
              value={formData.companyName.value}
              testId="companyName"
              onBlur={({ name, value }) => validateField(name, value)}
              errorMessage={formData.companyName.error}
              maxLength={isBRServer ? 50 : undefined}
              disabled={disableUserDataFields}
            />
          )}
        </Styles.NameWrapper>
        <Styles.TelephoneWrapper>
          <PhoneInput
            variant="fastcheckout"
            label={telephone.label}
            name="phone"
            key="phone"
            id="phone"
            inputValue={formData.phone.value || ''}
            onChangeInput={fieldData => handleFormChange('phone', fieldData)}
            onChangeSelect={value => handleFormChange('ddi', value)}
            onBlur={value => validateField('phone', value)}
            selected={countryCode}
            error={formData.phone.error}
            brand={COUNTRY}
            renderFlags={false}
            disabled={disableUserDataFields}
          />
        </Styles.TelephoneWrapper>
      </Styles.FormLine>
      {isCompany && (
        <Styles.FormLine>
          <TextField
            id="name"
            name="name"
            onChange={fieldData => handleFormChange('name', fieldData.currentTarget.value)}
            label={companyResponsible.label}
            value={formData.name.value}
            testId="companyResponsible"
            onBlur={({ name, value }) => validateField(name, value)}
            errorMessage={formData.name.error}
            maxLength={isBRServer ? 40 : undefined}
            disabled={disableUserDataFields}
          />
        </Styles.FormLine>
      )}
      <Styles.FormLine>
        {countriesSelectOptionsMemo.length > 0 && (
          <Styles.CountryWrapper>
            <SelectField
              withScroll
              options={countriesSelectOptionsMemo}
              positionAbsolute
              placeholder={country.label}
              id="country"
              name="country"
              onClickSelect={fieldData => handleFormChange('country', fieldData.value)}
              value={formData.country.value.name}
              filterable
              disabled={disableUserDataFields}
            />
          </Styles.CountryWrapper>
        )}
        <Styles.CepWrapper>
          <TextField
            id="postalCode"
            name="postalCode"
            onChange={fieldData => handleFormChange('postalCode', fieldData.currentTarget.value)}
            value={formData.postalCode.value}
            testId="postalCode"
            onBlur={({ name, value }) => validateField(name, value)}
            maxLength={POSTAL_CODE_LENGTH[brand]}
            helpText={formData.postalCode.error}
            status={formData.postalCode.error ? 'error' : 'default'}
            label={(isForeigner || !isBRServer) ? postalCode.label : cep.label}
            disabled={disableUserDataFields}
          />
          {!disableUserDataFields && (
            <Styles.DontKnowCepWrapper>
              <Link text={locale.dontKnowMyCep.label} href={getCepUrl} />
            </Styles.DontKnowCepWrapper>
          )}
        </Styles.CepWrapper>
      </Styles.FormLine>
      {loadingZipCode && (
      <Styles.LoadingWrapper>
        <IconLoading />
      </Styles.LoadingWrapper>
      )}
      <Styles.AddressComplementsWrapper $renderAddressFields={renderAddressFields}>
        <Styles.FormLine>
          <Styles.StateWrapper>
            <TextField
              disabled={isBRCountry || disableUserDataFields}
              label={state.label}
              id="state"
              name="state"
              onChange={fieldData => handleFormChange('state', fieldData.currentTarget.value)}
              onBlur={({ name, value }) => validateField(name, value)}
              helpText={formData.state.error}
              status={formData.state.error ? 'error' : 'default'}
              value={formData.state.value.name}
            />
          </Styles.StateWrapper>
          <Styles.CityWrapper>
            <TextField
              label={city.label}
              disabled={isBRCountry || disableUserDataFields}
              id="city"
              name="city"
              onChange={fieldData => handleFormChange('city', fieldData.currentTarget.value)}
              onBlur={({ name, value }) => validateField(name, value)}
              helpText={formData.city.error}
              status={formData.city.error ? 'error' : 'default'}
              value={formData.city.value.name}
            />
          </Styles.CityWrapper>
        </Styles.FormLine>
        <Styles.AddressWrapper>
          <TextField
            disabled={(isBRCountry && formData.address.disabled) || disableUserDataFields}
            label={address.label}
            id="address"
            name="address"
            onChange={fieldData => handleFormChange('address', fieldData.currentTarget.value)}
            onBlur={({ name, value }) => validateField(name, value)}
            helpText={formData.address.error}
            status={formData.address.error ? 'error' : 'default'}
            value={formData.address.value}
          />
        </Styles.AddressWrapper>
        <Styles.FormLine>
          <Styles.BlockWrapper>
            <TextField
              disabled={(isBRCountry && formData.neighborhood.disabled) || disableUserDataFields}
              label={block.label}
              id="neighborhood"
              name="neighborhood"
              onChange={fieldData => handleFormChange('neighborhood', fieldData.currentTarget.value)}
              onBlur={({ name, value }) => validateField(name, value)}
              helpText={formData.neighborhood.error}
              status={formData.neighborhood.error ? 'error' : 'default'}
              value={formData.neighborhood.value}
            />
          </Styles.BlockWrapper>
          <Styles.ComplementWrapper>
            <TextField
              label={complement.label}
              id="numberAndComplement"
              name="numberAndComplement"
              onChange={fieldData => handleFormChange('numberAndComplement', fieldData.currentTarget.value)}
              onBlur={({ name, value }) => validateField(name, value)}
              helpText={formData.numberAndComplement.error}
              status={formData.numberAndComplement.error ? 'error' : 'default'}
              value={formData.numberAndComplement.value}
              disabled={disableUserDataFields}
            />
          </Styles.ComplementWrapper>
        </Styles.FormLine>
      </Styles.AddressComplementsWrapper>
    </Styles.Content>
  );
};

export default withErrorBoundary(FastCheckoutUserData, 'FastCheckoutUserData');
